import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Form } from "@/components/ui/form";
import { ArrowLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Node, Edge } from "@xyflow/react";
import {
  useGetAiTemplateQuery,
  useCreateAiTemplateMutation,
  useUpdateAiTemplateMutation,
  useSetTemplatePromptMutation,
} from "@/store/admin_api";
import CreatePromptDialog from "./components/CreatePromptDialog";
import AssignAgentDialog from "./components/AssignAgentDialog";
import type { Prompt } from "@/model/interface/Prompt";
import { apiErrorMessage } from "@/util/apiError";
import SectionHeading from "./template-sections/SectionHeading";
import TemplateBasicsSection from "./template-sections/TemplateBasicsSection";
import TemplatePromptSection from "./template-sections/TemplatePromptSection";
import TemplateSkillsToolsSection from "./template-sections/TemplateSkillsToolsSection";
import TemplateVoiceEngineSection from "./template-sections/TemplateVoiceEngineSection";
import TemplateConversationSection from "./template-sections/TemplateConversationSection";
import TemplatePublishSection from "./template-sections/TemplatePublishSection";
import {
  TEMPLATE_FORM_DEFAULTS,
  TemplateFormValues,
  formValuesToTemplatePayload,
  templateConversationFlow,
  templateSchema,
  templateToFormValues,
} from "./templateForm";

export default function EditAiTemplate() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const isNew = !id;

  const { data: template, isLoading } = useGetAiTemplateQuery(id!, { skip: isNew });
  const [createTemplate, { isLoading: isCreating }] = useCreateAiTemplateMutation();
  const [updateTemplate, { isLoading: isUpdating }] = useUpdateAiTemplateMutation();
  const [setTemplatePrompt, { isLoading: isSettingPrompt }] = useSetTemplatePromptMutation();

  const [pendingPromptVersion, setPendingPromptVersion] = useState<{ id: string; label: string } | null>(null);
  const [createPromptOpen, setCreatePromptOpen] = useState(false);
  const [deployOpen, setDeployOpen] = useState(false);
  const [flowNodes, setFlowNodes] = useState<Node[]>([]);
  const [flowEdges, setFlowEdges] = useState<Edge[]>([]);
  const [isInitialized, setIsInitialized] = useState(isNew);

  const form = useForm<TemplateFormValues>({
    resolver: zodResolver(templateSchema),
    defaultValues: TEMPLATE_FORM_DEFAULTS,
  });

  const isSaving = isCreating || isUpdating;

  const handleAssignPrompt = async (promptVersionId: string | null, versionLabel?: string) => {
    if (!id) {
      setPendingPromptVersion(promptVersionId ? { id: promptVersionId, label: versionLabel ?? "" } : null);
      return;
    }
    try {
      await setTemplatePrompt({ id, promptVersionId }).unwrap();
      toast.success("Default prompt updated");
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to update prompt"));
    }
  };

  const handlePromptCreated = (created: Prompt) => {
    const firstVersion = created.versions?.[0];
    if (firstVersion) {
      handleAssignPrompt(firstVersion.id, `${created.name} · v${firstVersion.version}`);
    }
  };

  const handleFlowChange = (nodes: Node[], edges: Edge[]) => {
    setFlowNodes(nodes);
    setFlowEdges(edges);
  };

  const onSubmit = async (values: TemplateFormValues) => {
    const payload = formValuesToTemplatePayload(values, flowNodes, flowEdges);
    try {
      if (isNew) {
        const created = await createTemplate(payload).unwrap();
        if (pendingPromptVersion) {
          await setTemplatePrompt({ id: created.id, promptVersionId: pendingPromptVersion.id }).unwrap();
        }
        toast.success("Template created");
        navigate(`/platform-admin/ai-agents/templates/${created.id}`, { replace: true });
      } else {
        await updateTemplate({ id: id!, data: payload }).unwrap();
        toast.success("Template updated");
        navigate("/platform-admin/ai-agents/templates");
      }
    } catch (error: unknown) {
      toast.error(apiErrorMessage(error, "Failed to save template"));
    }
  };

  useEffect(() => {
    if (!template || isNew) {
      return;
    }
    form.reset(templateToFormValues(template));
    const flow = templateConversationFlow(template);
    setFlowNodes(flow?.nodes ?? []);
    setFlowEdges(flow?.edges ?? []);
    setIsInitialized(true);
  }, [template]);

  if (!isNew && (isLoading || !template || !isInitialized)) {
    return (
      <div className="space-y-6 p-4 text-foreground">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate("/platform-admin/ai-agents")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Edit Template</h1>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-12">
            <div className="space-y-4 animate-pulse">
              <div className="h-4 bg-muted rounded w-1/3" />
              <div className="h-10 bg-muted rounded" />
              <div className="h-4 bg-muted rounded w-1/4" />
              <div className="h-20 bg-muted rounded" />
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-4 text-foreground">
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="sm" onClick={() => navigate("/platform-admin/ai-agents")}>
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div>
          <h1 className="text-3xl font-bold tracking-tight">
            {isNew ? "New Template" : template?.name}
          </h1>
          <p className="text-muted-foreground">
            {isNew ? "Create a new AI agent template" : "Edit agent template configuration and conversation flow"}
          </p>
        </div>
      </div>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <SectionHeading step={1} title="Basics" description="What this agent is and how it is presented." />
          <TemplateBasicsSection />

          <SectionHeading step={2} title="Prompt and skills" description="What the agent knows and what it is allowed to do." />
          <TemplatePromptSection
            currentVersionId={isNew ? pendingPromptVersion?.id : template?.defaultPromptVersionId}
            currentVersionLabel={isNew ? pendingPromptVersion?.label : template?.defaultPromptVersionLabel}
            onAssignPrompt={handleAssignPrompt}
            isAssigningPrompt={isSettingPrompt}
            onRequestNewPrompt={() => setCreatePromptOpen(true)}
            savesImmediately={!isNew}
          />
          <TemplateSkillsToolsSection />

          <SectionHeading step={3} title="Voice and engine" description="How the agent sounds and what runs it." />
          <TemplateVoiceEngineSection />

          <SectionHeading step={4} title="Conversation" description="How calls open, close and behave in between." />
          <TemplateConversationSection
            flowNodes={flowNodes}
            flowEdges={flowEdges}
            onFlowChange={handleFlowChange}
          />

          <SectionHeading step={5} title="Publish" description="Set the status, save, then put the agent to work." />
          <TemplatePublishSection
            isNew={isNew}
            savedStatus={template?.status}
            onDeploy={() => setDeployOpen(true)}
            onCreateListing={() => navigate(`/platform-admin/ai-agents/listings?template=${id}`)}
          />

          <div className="flex justify-end gap-4">
            <Button type="button" variant="outline" onClick={() => navigate("/platform-admin/ai-agents/templates")}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSaving}>
              {isSaving && <Loader2 className="mr-1 h-4 w-4 animate-spin" />}
              {isNew ? "Create Template" : "Save Changes"}
            </Button>
          </div>
        </form>
      </Form>

      <CreatePromptDialog
        kind="AGENT"
        open={createPromptOpen}
        onOpenChange={setCreatePromptOpen}
        onCreated={handlePromptCreated}
      />
      <AssignAgentDialog
        key={id ?? "new"}
        open={deployOpen}
        onOpenChange={setDeployOpen}
        initialTemplateId={id}
        onCreated={(agent) => navigate(`/platform-admin/ai-agents/deployed/${agent.id}`)}
      />
    </div>
  );
}
